import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, ShieldCheck, ArrowLeft, Minus, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHero, Reveal, useSettings } from "@/components/shared";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LUNCH = ["11:45", "12:00", "12:15", "12:30", "12:45", "13:00", "13:15", "13:30", "13:45", "14:00"];
const DINNER = ["19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30", "23:00"];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  date: "",
  time: "",
  guests: "2",
  message: "",
};

const parsePrice = (p) =>
  parseFloat((p || "").replace("€", "").replace(/\s/g, "").replace(",", ".")) || 0;

export default function Reservation() {
  const settings = useSettings();
  const deposit = parseFloat(settings?.deposit_per_person) || 15;
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState("form");
  const [conditions, setConditions] = useState(false);
  const [plats, setPlats] = useState([]);
  const [preOrder, setPreOrder] = useState({});
  const [blockedSlots, setBlockedSlots] = useState([]);

  useEffect(() => {
    if (!form.date) {
      setBlockedSlots([]);
      return;
    }
    api
      .get(`/blocked-slots?date=${form.date}`)
      .then((r) => setBlockedSlots(r.data))
      .catch(() => setBlockedSlots([]));
  }, [form.date]);

  const isTimeBlocked = (t) => blockedSlots.some((b) => !b.time || b.time === t);
  const dayFullyBlocked = form.date && blockedSlots.some((b) => !b.time);

  const guests = parseInt(form.guests || "1", 10);
  const isGroup = guests >= 5;

  useEffect(() => {
    if (step === "confirm" && isGroup && plats.length === 0) {
      api
        .get("/menu")
        .then((r) => setPlats((r.data?.plats || []).filter((p) => parsePrice(p.price) > 0)))
        .catch(() => {});
    }
  }, [step, isGroup, plats.length]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const preOrderList = Object.values(preOrder);
  const preOrderQty = preOrderList.reduce((s, c) => s + c.qty, 0);
  const estimated = preOrderList.reduce((s, c) => s + parsePrice(c.item.price) * c.qty, 0);
  const totalAmount = isGroup ? estimated * 0.3 : deposit * guests;
  const total = totalAmount.toFixed(2).replace(".", ",");

  const addDish = (item) =>
    setPreOrder((c) => ({ ...c, [item.id]: { item, qty: (c[item.id]?.qty || 0) + 1 } }));
  const decDish = (id) =>
    setPreOrder((c) => {
      const qty = (c[id]?.qty || 0) - 1;
      const next = { ...c };
      if (qty <= 0) delete next[id];
      else next[id] = { ...next[id], qty };
      return next;
    });

  const submit = (e) => {
    e.preventDefault();
    if (!form.time) {
      toast.error("Merci de choisir un horaire.");
      return;
    }
    setStep("confirm");
    window.scrollTo(0, 0);
  };

  const pay = async () => {
    if (!conditions) {
      toast.error("Merci d'accepter les conditions d'annulation.");
      return;
    }
    if (isGroup && preOrderQty < guests) {
      toast.error(`Merci de choisir au minimum ${guests} plats (un par personne).`);
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/reservations", {
        ...form,
        guests,
        pre_order: preOrderList.map((c) => ({ menu_item_id: c.item.id, quantity: c.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSending(false);
    }
  };

  return (
    <div data-testid="reservation-page">
      <PageHero overline="Réservation" title="Réserver une table" testId="reservation-hero">
        <p>
          Complétez le formulaire ci-dessous : nous confirmons chaque demande par téléphone ou par
          email.
        </p>
      </PageHero>

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          {step === "confirm" ? (
            <Reveal>
              <div
                className="rounded-lg border border-white/10 bg-coal/60 p-8 md:p-10"
                data-testid="reservation-confirm"
              >
                <h2 className="text-2xl font-semibold tracking-tight text-ivory">Récapitulatif</h2>
                <dl className="mt-6 space-y-2.5 text-sm">
                  {[
                    ["Nom", form.name],
                    ["Date", form.date],
                    ["Horaire", form.time.replace(":", "h")],
                    ["Convives", `${form.guests} personne(s)`],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-4">
                      <dt className="text-ivory/45">{label}</dt>
                      <dd className="text-ivory">{value}</dd>
                    </div>
                  ))}
                </dl>

                {isGroup && (
                  <div className="mt-6 rounded-md border border-white/10 p-5" data-testid="preorder-section">
                    <h3 className="text-sm font-semibold text-ivory">
                      Pré-commande des plats (groupe de {guests} personnes)
                    </h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-ivory/45">
                      Choisissez au minimum un plat par personne. L'acompte correspond à 30 % du
                      montant estimé.
                    </p>
                    <div className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1">
                      {plats.map((item) => {
                        const inOrder = preOrder[item.id];
                        return (
                          <div
                            key={item.id}
                            className="flex items-center gap-3 rounded-md border border-white/5 px-3 py-2.5"
                            data-testid={`preorder-item-${item.id}`}
                          >
                            <span className="min-w-0 flex-1 truncate text-xs text-ivory/80">
                              {item.name_fr}
                            </span>
                            <span className="shrink-0 text-xs text-gold">{item.price}</span>
                            {inOrder ? (
                              <div className="flex shrink-0 items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => decDish(item.id)}
                                  aria-label="Retirer"
                                  className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold"
                                  data-testid={`preorder-dec-${item.id}`}
                                >
                                  <Minus className="h-3 w-3" strokeWidth={1.5} />
                                </button>
                                <span className="w-5 text-center text-xs font-semibold text-ivory">
                                  {inOrder.qty}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => addDish(item)}
                                  aria-label="Ajouter"
                                  className="flex h-7 w-7 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold"
                                  data-testid={`preorder-inc-${item.id}`}
                                >
                                  <Plus className="h-3 w-3" strokeWidth={1.5} />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => addDish(item)}
                                className="flex h-7 shrink-0 items-center gap-1 rounded-md border border-white/10 px-2.5 text-[11px] text-ivory/60 transition-colors hover:border-gold hover:text-gold"
                                data-testid={`preorder-add-${item.id}`}
                              >
                                <Plus className="h-3 w-3" strokeWidth={1.5} />
                                Ajouter
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p
                      className={`mt-3 text-xs ${preOrderQty >= guests ? "text-gold" : "text-ivory/45"}`}
                      data-testid="preorder-count"
                    >
                      {preOrderQty} / {guests} plats choisis
                    </p>
                  </div>
                )}

                <div className="mt-6 rounded-md border border-gold/30 bg-gold/5 p-5">
                  {isGroup ? (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-ivory/70">
                          Montant estimé ({preOrderQty} plats)
                        </span>
                        <span className="text-ivory">
                          {estimated.toFixed(2).replace(".", ",")} €
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-sm text-ivory/70">Acompte de 30 %</span>
                        <span className="text-lg font-bold text-gold" data-testid="reservation-total">
                          {total} €
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ivory/70">
                        Acompte ({guests} × {deposit.toFixed(2).replace(".", ",")} €)
                      </span>
                      <span className="text-lg font-bold text-gold" data-testid="reservation-total">
                        {total} €
                      </span>
                    </div>
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-ivory/45">
                    L'acompte sera automatiquement déduit de votre facture finale au restaurant.
                  </p>
                </div>

                <div className="mt-6 rounded-md border border-white/10 p-5">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-ivory">
                    <ShieldCheck className="h-4 w-4 text-gold" strokeWidth={1.5} />
                    Conditions d'annulation
                  </h3>
                  <ul className="mt-3 list-disc space-y-1.5 pl-5 text-xs leading-relaxed text-ivory/50">
                    <li>Annulation plus de 24 h avant : remboursement possible selon nos CGV.</li>
                    <li>
                      Réservation du jour même : remboursable uniquement si elle est annulée plus
                      d'une heure avant l'heure réservée.
                    </li>
                    <li>Réservation effectuée moins d'une heure avant : non remboursable.</li>
                    <li>Absence sans annulation (« No Show ») : acompte intégralement conservé.</li>
                  </ul>
                  <label
                    className="mt-4 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-ivory/70"
                    data-testid="reservation-conditions-label"
                  >
                    <input
                      type="checkbox"
                      checked={conditions}
                      onChange={(e) => setConditions(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[#C4704F]"
                      data-testid="reservation-conditions-checkbox"
                    />
                    J'ai lu et j'accepte les conditions d'annulation et les Conditions Générales de
                    Vente.
                  </label>
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => setStep("form")}
                    className="btn-outline flex-1"
                    data-testid="reservation-back-button"
                  >
                    <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                    Retour
                  </button>
                  <button
                    onClick={pay}
                    disabled={sending || !conditions}
                    className="btn-primary flex-1"
                    data-testid="reservation-pay-button"
                  >
                    <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                    {sending ? "Redirection…" : `Payer l'acompte — ${total} €`}
                  </button>
                </div>
                <p className="mt-4 text-center text-[11px] text-ivory/35">
                  Paiement sécurisé par Stripe.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form
                onSubmit={submit}
                className="space-y-5 rounded-lg border border-white/10 bg-coal/60 p-8 md:p-10"
                data-testid="reservation-form"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <input
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Nom et prénom"
                    className="input-dark"
                    data-testid="reservation-name-input"
                  />
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Téléphone"
                    className="input-dark"
                    data-testid="reservation-phone-input"
                  />
                </div>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Email"
                  className="input-dark"
                  data-testid="reservation-email-input"
                />
                <div className="grid gap-5 sm:grid-cols-3">
                  <input
                    required
                    type="date"
                    value={form.date}
                    onChange={update("date")}
                    min={new Date().toISOString().split("T")[0]}
                    className="input-dark [color-scheme:dark]"
                    data-testid="reservation-date-input"
                  />
                  <Select
                    value={form.time}
                    onValueChange={(v) => setForm((f) => ({ ...f, time: v }))}
                  >
                    <SelectTrigger
                      className="!h-12 border-white/10 bg-white/5 text-sm text-ivory"
                      data-testid="reservation-time-select"
                    >
                      <SelectValue placeholder="Horaire" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-coal text-ivory">
                      <SelectGroup>
                        <SelectLabel className="text-gold">Déjeuner</SelectLabel>
                        {LUNCH.map((t) => (
                          <SelectItem key={t} value={t} disabled={isTimeBlocked(t)}>
                            {t.replace(":", "h")}
                            {isTimeBlocked(t) ? " — indisponible" : ""}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-gold">Dîner</SelectLabel>
                        {DINNER.map((t) => (
                          <SelectItem key={t} value={t} disabled={isTimeBlocked(t)}>
                            {t.replace(":", "h")}
                            {isTimeBlocked(t) ? " — indisponible" : ""}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {dayFullyBlocked && (
                    <p className="text-xs text-copper sm:col-span-3" data-testid="reservation-day-blocked">
                      Cette journée n'est pas disponible à la réservation. Merci de choisir une autre date.
                    </p>
                  )}
                  <Select
                    value={form.guests}
                    onValueChange={(v) => setForm((f) => ({ ...f, guests: v }))}
                  >
                    <SelectTrigger
                      className="!h-12 border-white/10 bg-white/5 text-sm text-ivory"
                      data-testid="reservation-guests-select"
                    >
                      <SelectValue placeholder="Convives" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-coal text-ivory">
                      {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} {n === 1 ? "personne" : "personnes"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Précisions éventuelles (allergies, occasion, demande particulière…)"
                  rows={4}
                  className="input-dark !h-auto py-4"
                  data-testid="reservation-message-input"
                />
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary w-full"
                  data-testid="reservation-submit-button"
                >
                  {sending ? "Envoi…" : "Envoyer ma demande"}
                </button>
                <p className="text-center text-xs leading-relaxed text-ivory/35">
                  Vos données sont utilisées uniquement pour traiter votre réservation. Consultez
                  notre politique de confidentialité.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}

export function ReservationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/reservations/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="reservation-success-page">
      <PageHero
        overline="Réservation"
        title={state === "paid" ? "Demande reçue" : "Confirmation en cours"}
        testId="reservation-hero"
      />
      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <div className="rounded-lg border border-gold/30 bg-coal/60 p-12 text-center">
              {state === "loading" && (
                <p className="text-sm text-ivory/60" data-testid="payment-loading">
                  Vérification du paiement…
                </p>
              )}
              {state === "paid" && (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-8 text-2xl font-semibold tracking-tight text-ivory">Merci !</h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ivory/55" data-testid="payment-confirmed">
                    Votre acompte a bien été reçu et votre demande de réservation est enregistrée.
                    Elle est <strong className="text-gold">en attente de confirmation</strong> par
                    l'établissement : vous recevrez un email dès qu'elle sera acceptée. L'acompte
                    sera déduit de votre facture finale au restaurant.
                  </p>
                </>
              )}
              {state === "pending" && (
                <p className="text-sm text-ivory/60" data-testid="payment-pending">
                  Paiement en cours de traitement. Vous recevrez un email de confirmation.
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-red-400" data-testid="payment-error">
                  Impossible de vérifier le paiement. Merci de nous contacter sur WhatsApp.
                </p>
              )}
              <Link to="/" className="btn-outline mt-10" data-testid="reservation-success-home">
                Retour à l'accueil
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export function ReservationAnnulee() {
  return (
    <div data-testid="reservation-cancel-page">
      <PageHero overline="Réservation" title="Paiement annulé" testId="reservation-hero">
        <p>
          Le paiement de l'acompte a été annulé : votre réservation n'a pas été confirmée. Vous
          pouvez reprendre votre réservation à tout moment.
        </p>
      </PageHero>
      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl text-center">
          <Link to="/reservation" className="btn-primary" data-testid="reservation-retry-button">
            Reprendre ma réservation
          </Link>
        </div>
      </section>
    </div>
  );
}
